import { ExternalLink, Mail, ShieldQuestion, HeartHandshake, Phone, Globe } from "lucide-react";
import { GoldBadge, Bullets, Reveal } from "@/components/shared";

const BEM_CASES = [
  "Housing, money, food and debt crises",
  "Drug and alcohol addiction and abuse",
  "Immediate crisis risk — self-harm or suicide",
  "Domestic violence or abuse",
  "Neglect or sexual abuse",
  "Kidnapping and abduction cases",
  "Parents and children in need — see the Parents section in the app",
  "Special needs awareness — specialist teams around the world",
];

const HARMONY_SERVICES = [
  "Suicide intervention and suicide prevention",
  "One-to-one support",
  "Community support and referral advice",
  "Private therapy",
  "Neurodiversity awareness and neurodivergent-friendly community",
  "Parent and carer support",
  "Wellbeing activities, workshops and resources",
  "Advocacy and empowerment",
  "Information and signposting — benefits, education, healthcare and more",
  "Practical help and real-life solutions",
];

const HARMONY_TEAM = [
  { name: "Chris", phone: "07511 151569" },
  { name: "Bev", phone: "07451 234556" },
  { name: "Vicky", phone: "07518 165060" },
];

export default function PartnersSection() {
  return (
    <section
      data-testid="partners-section"
      className="border-t border-white/10 bg-[#0D111B] grain-overlay relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <Reveal>
          <div className="max-w-3xl">
            <GoldBadge testId="partners-badge">Allied Support Services</GoldBadge>
            <h2 className="font-display mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-50">
              Partners in Protection
            </h2>
            <div className="h-[2px] w-16 bg-gradient-to-r from-amber-400 via-amber-600 to-transparent my-4" />
            <p className="text-base sm:text-lg leading-relaxed text-slate-300">
              People Against Intentional Neglect stands alongside Behind Every Mask and Harmony
              Harbour Mental Health Services — so no survivor falls through the cracks, in Australia,
              the UK, or anywhere the mask gets heavy.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="spotlight-frame relative mt-10 rounded-xl overflow-hidden border border-amber-500/25 shadow-2xl">
            <img
              src="/assets/bem-harmony-banner.jpeg"
              alt="Harmony Harbour UK Mental Health Services, Behind Every Mask, and P.A.I.N. — Empathy in Action, 24/7 support, You are not alone"
              className="w-full h-auto object-cover"
              data-testid="partners-banner"
            />
          </div>
        </Reveal>

        {/* BEHIND EVERY MASK */}
        <div className="mt-14 space-y-8">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-lg bg-[#1D4ED8]/20 border border-amber-500/25">
                <ShieldQuestion className="h-6 w-6 text-amber-400" />
              </span>
              <div>
                <h3 className="font-display text-2xl sm:text-3xl font-semibold text-slate-50">
                  Behind Every Mask
                </h3>
                <p className="font-code text-[10px] uppercase tracking-[0.2em] text-amber-400/90">
                  BEM Crisis App — Behind Every Mask is a true story
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <div data-testid="bem-founder-card" className="card-pain p-6 sm:p-8">
              <p className="font-code text-[10px] uppercase tracking-[0.2em] text-amber-400/90 mb-4">
                Meet The Founder — Jordan Guy
              </p>
              <div className="space-y-4 text-base sm:text-lg leading-relaxed text-slate-300">
                <p>
                  "I created Behind Every Mask after losing my dad to suicide and going through my
                  own struggles with mental health, addiction and homelessness. I know how hard it
                  can be to ask for help, and how much it matters when someone listens and helps you
                  find a way forward."
                </p>
                <p className="font-display italic text-amber-200/95">
                  "We stand for hope, dignity and the belief that every story deserves the chance to
                  continue."
                </p>
              </div>
              <p className="mt-4 text-sm text-slate-400">
                — Jordan Guy, Founder of Behind Every Mask & Creator of the BEM Crisis App
              </p>
            </div>
          </Reveal>

          <div className="grid gap-6 lg:grid-cols-2">
            <Reveal delay={0.05}>
              <div data-testid="partner-bem-card" className="card-pain p-6 sm:p-8 h-full">
                <h4 className="font-display text-lg font-medium text-slate-50 mb-4">About The App</h4>
                <Bullets
                  items={[
                    "A crisis referral and wellbeing app connecting people of any age, anywhere in the world, with support",
                    "Free and anonymous — no account, no charges, ever",
                    "Compassion without judgement: crisis support, community outreach, and a future retreat",
                    "Real stories from people who have worn the mask",
                  ]}
                />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div data-testid="bem-scope-card" className="card-pain p-6 sm:p-8 h-full">
                <h4 className="font-display text-lg font-medium text-slate-50 mb-4">
                  What BEM Helps With — all types of cases and needs
                </h4>
                <Bullets items={BEM_CASES} />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div data-testid="bem-gethelp-card" className="card-pain p-6 sm:p-8">
              <h4 className="font-display text-lg font-medium text-slate-50 mb-4">
                How To Get Help
              </h4>
              <Bullets
                items={[
                  "Open the app and go to the Get Help section",
                  "Make a report — be open and honest; every word stays between you and the team",
                  "BEM finds the connections and answers for your exact situation",
                  "You are guided through it all, step by step, for as long as you need",
                  "Immediate danger? Call 999 (UK) or 000 (Australia) first",
                ]}
              />
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="https://bem-crisis.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="partner-bem-link"
                  className="btn-royal inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-white"
                >
                  Open the BEM App — Press for Help Now
                  <ExternalLink className="h-4 w-4" />
                </a>
                <a
                  href="mailto:b.e.m.crisis.referral@gmail.com"
                  data-testid="partner-bem-email"
                  className="btn-gold-outline inline-flex items-center gap-2 rounded-md px-4 py-3 text-sm font-semibold text-amber-300"
                >
                  <Mail className="h-4 w-4" />
                  Email BEM
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        {/* HARMONY HARBOUR */}
        <div className="mt-16 space-y-8">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-lg bg-[#1D4ED8]/20 border border-amber-500/25">
                <HeartHandshake className="h-6 w-6 text-amber-400" />
              </span>
              <div>
                <h3 className="font-display text-2xl sm:text-3xl font-semibold text-slate-50">
                  Harmony Harbour Mental Health Services
                </h3>
                <p className="font-code text-[10px] uppercase tracking-[0.2em] text-amber-400/90">
                  UK — A safe harbour for brighter tomorrows
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <div className="card-pain p-6 sm:p-8" data-testid="harmony-intro-card">
              <div className="space-y-4 text-base sm:text-lg leading-relaxed text-slate-300 max-w-3xl">
                <p>
                  A mental health charity-run organisation, operated by volunteers with one mission
                  and one purpose — <span className="text-amber-300 font-semibold">to save lives</span> —
                  and to have a positive impact for those finding everyday life a struggle.
                </p>
                <p>
                  A supportive, inclusive community focused on mental health and neurodiversity —
                  because every mind is unique, and every person deserves to feel heard, valued and
                  empowered. All volunteers are highly trained and certified, and all carry lived
                  experience, building trusting professional relationships.
                </p>
                <p className="font-display italic text-amber-200/95">
                  "We want to share your journey with you. You are not alone."
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid gap-6 lg:grid-cols-2">
            <Reveal delay={0.05}>
              <div data-testid="harmony-services-card" className="card-pain p-6 sm:p-8 h-full">
                <h4 className="font-display text-lg font-medium text-slate-50 mb-4">
                  Harmony Harbour's Services
                </h4>
                <Bullets items={HARMONY_SERVICES} />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div data-testid="harmony-contact-card" className="card-pain p-6 sm:p-8 h-full">
                <h4 className="font-display text-lg font-medium text-slate-50 mb-4">
                  Reach The Harbour
                </h4>
                <ul className="space-y-3 text-sm sm:text-base">
                  <li className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
                    <span className="text-slate-300">24/7 Crisis Number</span>
                    <a href="tel:08001613122" data-testid="harmony-crisis-number" className="font-code font-bold text-amber-400">
                      0800 161 3122
                    </a>
                  </li>
                  <li className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
                    <span className="text-slate-300">Website</span>
                    <a
                      href="https://www.harmonyharbour.co.uk"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="harmony-website-link"
                      className="font-code text-sm text-amber-400 inline-flex items-center gap-1.5"
                    >
                      harmonyharbour.co.uk <Globe className="h-3.5 w-3.5" />
                    </a>
                  </li>
                  <li className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
                    <span className="text-slate-300">Email</span>
                    <a href="mailto:enquiries@harmonyharbour.co.uk" data-testid="partner-harmony-email" className="font-code text-sm text-amber-400">
                      enquiries@harmonyharbour.co.uk
                    </a>
                  </li>
                  <li className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
                    <span className="text-slate-300">Open Hours</span>
                    <span className="font-code text-xs text-slate-100 text-right">
                      Mon–Thu 7am–11pm<br />Fri–Sun 7am–2am
                    </span>
                  </li>
                  {HARMONY_TEAM.map((t) => (
                    <li key={t.name} className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
                      <span className="text-slate-300">{t.name} (WhatsApp)</span>
                      <a href={`tel:${t.phone.replace(/\s/g, "")}`} className="font-code text-sm text-slate-100 inline-flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5 text-amber-400" /> {t.phone}
                      </a>
                    </li>
                  ))}
                  <li className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
                    <span className="text-slate-300">TikTok</span>
                    <a
                      href="https://www.tiktok.com/@harmonyharbouruk"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="harmony-tiktok-link"
                      className="font-code text-sm text-amber-400"
                    >
                      @harmonyharbouruk
                    </a>
                  </li>
                  <li className="pt-1 text-xs text-slate-400 leading-relaxed">
                    In a mental health emergency, or if the service is closed: call NHS 111 (option 2),
                    dial 999, or go to your nearest A&E department.
                  </li>
                </ul>
              </div>
            </Reveal>
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <Reveal delay={0.05}>
              <div className="spotlight-frame relative rounded-xl overflow-hidden border border-amber-500/25 shadow-2xl">
                <img
                  src="/assets/harmony-services.jpg"
                  alt="Harmony Harbour UK services — suicide intervention and prevention, one-to-one support, community support, private therapy, 24/7 crisis number 0800 161 3122"
                  className="w-full h-auto object-cover"
                  data-testid="harmony-poster-services"
                />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="spotlight-frame relative rounded-xl overflow-hidden border border-amber-500/25 shadow-2xl">
                <img
                  src="/assets/harmony-hub.jpg"
                  alt="Harmony Harbour UK — Who Are We: support, understand, accept, include, empower, belong — neurodiversity awareness hub"
                  className="w-full h-auto object-cover"
                  data-testid="harmony-poster-hub"
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="spotlight-frame relative rounded-xl overflow-hidden border border-amber-500/25 shadow-2xl max-w-2xl mx-auto">
              <img
                src="/assets/alliance-poster.jpg"
                alt="Harmony Harbour UK, Behind Every Mask and P.A.I.N. — Professional Crisis Support for Mental Health Conditions, working together for safer, stronger futures"
                className="w-full h-auto object-cover"
                data-testid="alliance-poster"
              />
            </div>
          </Reveal>

          <Reveal>
            <p className="text-center font-code text-[10px] sm:text-xs uppercase tracking-[0.25em] text-slate-500">
              Listen • Support • Include • Respect • Empower • Change Lives
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
